import ContentfulImage from "@/components/ContentfulImage/ContentfulImage";
import {
  BodyMedium14,
  H4Css,
  H5Css,
  H6Css,
  H7Css,
  Heading,
  ListItem,
} from "@/components/Typography";
import { mq } from "@/constants/breakpoints";
import { darkColor, lightBlueColor, textColor } from "@/constants/colors";
import { blogPostPath, signUpPath } from "@/constants/paths";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { css } from "@emotion/react";
import Link from "next/link";
import slugify from "slugify";

export const slugifyHeading = (type: "h2" | "h3", heading: string) => {
  return slugify(`${type}-${heading}`, { lower: true });
};

/**
 * Recursively extracts plain text from Contentful rich text JSON.
 * @param node The root node (usually { nodeType, content, ... })
 * @returns Plain text string
 */
export function richTextToPlainText(node: any): string {
  if (!node) return "";
  if (typeof node === "string") return node;
  if (Array.isArray(node)) return node.map(richTextToPlainText).join(" ");
  if (node.nodeType === "text") return node.value || "";
  if (node.content) return node.content.map(richTextToPlainText).join(" ");
  return "";
}

const renderOptions = (links?: any) => {
  // create an asset map
  const assetMap = new Map();
  // loop through the assets and add them to the map
  if (links) {
    for (const asset of links.assets.block) {
      if (asset) {
        assetMap.set(asset.sys.id, asset);
      }
    }
  }

  // create an entry map
  const entryMap = new Map();
  // loop through the block linked entries and add them to the map
  // for (const entry of links.entries.block) {
  //   entryMap.set(entry.sys.id, entry);
  // }

  // loop through the inline linked entries and add them to the map
  // for (const entry of links.entries.inline) {
  //   entryMap.set(entry.sys.id, entry);
  // }

  // loop through the hyperlink linked entries and add them to the map
  if (links) {
    for (const entry of links.entries.hyperlink) {
      entryMap.set(entry.sys.id, entry);
    }
  }

  return {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
        const { value } = node.content[0];
        if (value === "TABLE_OF_CONTENTS") {
          return null;
        } else if (value.match(/^https:\/\/www\.youtube\.com\/.+/)) {
          try {
            const match = value.match(
              /\?v=([0-9A-Za-z_-]+)|\/embed\/([0-9A-Za-z_-]+)/
            );
            const id = match[1] || match[2];
            return (
              <div
                css={css`
                  position: relative;
                  padding-bottom: 56.25%;
                  height: 0;
                  overflow: hidden;
                  max-width: 100%;
                  margin-top: 20px;
                  border-radius: 12px;
                  iframe,
                  object,
                  embed {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                  }
                `}
              >
                <iframe
                  title={value}
                  src={`https://www.youtube.com/embed/${id}`}
                  frameBorder="0"
                  allowFullScreen
                ></iframe>
              </div>
            );
          } catch (e) {
            console.log("e", e);
            return <div>ERROR EMBEDDING YOUTUBE VIDEO</div>;
          }
        }
        return (
          <BodyMedium14
            css={css`
              color: ${textColor};
              &:not(:first-of-type) {
                margin-top: 20px;
              }
            `}
          >
            {children}
          </BodyMedium14>
        );
      },
      [BLOCKS.OL_LIST]: (node: any, children: any) => {
        return (
          <ol
            css={css`
              color: ${textColor};
              padding-left: 20px;
              li {
                margin-top: 18px;
              }
            `}
          >
            {children}
          </ol>
        );
      },
      [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
        return <ListItem>{children}</ListItem>;
      },
      [BLOCKS.UL_LIST]: (node: any, children: any) => {
        return (
          <ul
            css={css`
              color: ${lightBlueColor};
              padding-left: 20px;
              li {
                margin-top: 18px;
              }
            `}
          >
            {children}
          </ul>
        );
      },
      [BLOCKS.HEADING_2]: (node: any, children: any) => {
        const id = slugifyHeading("h2", node.content[0].value);
        return (
          <Heading
            level={2}
            id={id}
            css={css`
              color: ${darkColor};
              padding-top: 60px;
              ${H7Css}
              ${mq["lg"]} {
                ${H4Css}
              }
            `}
          >
            {children}
          </Heading>
        );
      },
      [BLOCKS.HEADING_3]: (node: any, children: any) => {
        const id = slugifyHeading("h3", node.content[0].value);
        return (
          <Heading
            id={id}
            level={3}
            css={css`
              color: ${darkColor};
              padding-top: 20px;
              font-weight: 400;
              font-size: 18px;
              line-height: 130.02%;
              ${mq["lg"]} {
                ${H5Css}
              }
            `}
          >
            {children}
          </Heading>
        );
      },
      [BLOCKS.HEADING_4]: (node: any, children: any) => {
        return (
          <Heading
            level={4}
            css={css`
              color: ${darkColor};
              padding-top: 20px;
              font-weight: 400;
              font-size: 16px;
              line-height: 130%;
              ${mq["lg"]} {
                ${H6Css}
              }
            `}
          >
            {children}
          </Heading>
        );
      },
      [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
        const { title, height, width, url, contentType, description } =
          assetMap.get(node.data.target.sys.id) || {};
        if (!url) return null;

        if (contentType.startsWith("video")) {
          return (
            <div
              css={css`
                margin-top: 30px;
              `}
            >
              <video
                autoPlay
                loop
                playsInline
                muted
                css={css`
                  width: 100%;
                  height: auto;
                  border-radius: 12px;
                `}
              >
                <source src={url} type={contentType!} />
              </video>
            </div>
          );
        }

        const Wrapper = (props: any) =>
          description?.startsWith("href=") ? (
            <a href={description.replace("href=", "")}>{props.children}</a>
          ) : (
            props.children
          );

        return (
          <Wrapper>
            <ContentfulImage
              alt={title}
              src={url}
              width={width}
              height={height}
              css={css`
                width: 100%;
                height: auto;
                margin-top: 30px;
                border-radius: 12px;
              `}
            />
          </Wrapper>
        );
      },
      [INLINES.ENTRY_HYPERLINK]: (node: any, children: any) => {
        const { __typename, slug } = entryMap.get(node.data.target.sys.id);
        let href = "";
        switch (__typename) {
          case "BlogPost":
            href = blogPostPath(slug);
            break;
          case "FeaturePage":
            href = `/features/${slug}`;
            break;
          case "SportPage":
            href = `/sports/${slug}`;
            break;
          case "SportsPageV2":
            href = `/sports/${slug}`;
            break;
        }

        return (
          <Link
            href={href}
            css={css`
              color: ${lightBlueColor};
              font-weight: inherit;
              font-family: inherit;
              transition: color 0.2s ease-in-out;
              &:hover {
                color: #45abe7;
              }
            `}
          >
            {children}
          </Link>
        );
      },
      [BLOCKS.QUOTE]: (node: any, children: any) => {
        return (
          <Heading
            css={css`
              font-weight: 350;
              font-size: 12px;
              line-height: 140.02%;
              text-align: center;
              margin-top: 16px;
            `}
          >
            {children}
          </Heading>
        );
      },
      [INLINES.HYPERLINK]: (node: any, children: any) => {
        const { uri } = node.data;
        const href = uri === "jwlink_signup" ? signUpPath : uri;
        return (
          <Link
            href={href}
            css={css`
              color: ${lightBlueColor};
              font-weight: inherit;
              transition: color 0.2s ease-in-out;
              &:hover {
                color: #45abe7;
              }
            `}
          >
            {children}
          </Link>
        );
      },
    },
  };
};

export default renderOptions;
